import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Line, LineChart, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import * as RechartsPrimitive from "recharts";

import { CampaignAlgoAnalyticsBlock } from "@/components/CampaignAnalytics";
import { PreviewPost } from "@/components/PreviewFeed";
import { Spinner } from "@/components/Spinner";
import { ChartContainer } from "@/components/ui/chart";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Label } from "@/components/ui/label";
import {
  CampaignAnalytic,
  CampaignAnalyticsSummaryType,
  campaignQueryOptions,
  feedCampaignQueryOptions,
  getCurrentUserQueryOptions,
  getHydratedPostQueryOptions,
} from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/view/$algoId/_layout/",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { campaignId, algoId },
  }) => {
    const [campaign] = await Promise.all([
      queryClient.ensureQueryData(campaignQueryOptions(parseInt(campaignId))),
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
    ]);

    const postData: Record<string, CampaignAnalyticsSummaryType> = {};

    await Promise.all(
      campaign.sticky_posts.map(async (post) => {
        postData[post.uri] = await queryClient.ensureQueryData(
          feedCampaignQueryOptions({
            campaignId: [parseInt(campaignId)],
            algorithmId: [parseInt(algoId)],
            postUri: [post.uri],
            days: 30,
          }),
        );
      }),
    );

    const dataData: Record<
      string,
      Array<CampaignAnalytic & { uri: string }>
    > = {};

    for (const [uri, analytics] of Object.entries(postData)) {
      if (!analytics.details) {
        continue;
      }

      for (const analytic of analytics.details) {
        if (!dataData[analytic.view_date]) {
          dataData[analytic.view_date] = [];
        }
        dataData[analytic.view_date]?.push({
          ...analytic,
          uri,
        });
      }
    }

    const test = [];
    const keys = new Set<string>();

    for (const [date, analytics] of Object.entries(dataData)) {
      const obj = analytics.reduce((acc, cur) => {
        return {
          ...acc,
          date,
          [`total_views_${cur.uri}`]: cur.view_count,
          [`unique_user_views_${cur.uri}`]: cur.unique_user_count,
        };
      }, {});
      test.push(obj);
      for (const key of Object.keys(obj)) {
        keys.add(key);
      }
    }

    return { postData: test, keys };
  },
});

const lineColors = {
  amber: "text-amber-10 dark:text-amberdark-10",
  cyan: "text-cyan-10 dark:text-cyandark-10",
  grass: "text-grass-10 dark:text-grassdark-10",
  grape: "text-grape-10 dark:text-grapedark-10",
  red: "text-red-10 dark:text-reddark-10",
  blue: "text-blue-10 dark:text-bluedark-10",
  pink: "text-pink-10 dark:text-pinkdark-10",
  plum: "text-plum-10 dark:text-plumdark-10",
  purple: "text-purple-10 dark:text-purpledark-10",
  tomato: "text-tomato-10 dark:text-tomatodark-10",
  teal: "text-teal-10 dark:text-tealdark-10",
};
const colors = Object.values(lineColors);

function LegendPostPreview({ uri }: { uri: string }) {
  const { data: post, isPending } = useQuery(getHydratedPostQueryOptions(uri));

  if (isPending || !post) {
    return <Spinner />;
  }

  return <PreviewPost record={post} />;
}

const ChartLegendContent = ({
  ref,
  className,
  payload,
  verticalAlign = "bottom",
}: React.ComponentProps<"div"> &
  Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign">) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-center gap-4",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className,
      )}
    >
      {payload.map((item, index) => {
        const uri = (item.value as string)
          .replace(/total_views_/, "")
          .replace(/unique_user_views_/, "");
        return (
          <HoverCard key={item.value as string}>
            <HoverCardTrigger>
              <div
                className={cn(
                  (item.payload as { className: string } | undefined)
                    ?.className,
                  "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-slate-dim",
                )}
              >
                <div className="h-4 w-4 rounded bg-current" />
                <span>Post {index + 1}</span>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-[400px]">
              <LegendPostPreview uri={uri} />
            </HoverCardContent>
          </HoverCard>
        );
      })}
    </div>
  )
};

function PostGraph({
  type = "total_views",
}: {
  type: "total_views" | "unique_user_views";
}) {
  const { postData, keys } = Route.useLoaderData();

  if (!postData.length) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-dim p-4">
        No data available.
      </div>
    );
  }

  const lines = Array.from(keys)
    .filter((key) => key.includes(type))
    .map((item, index) => ({
      [item]: {
        color: colors[index % colors.length],
      },
    }))
    .reduce((acc, cur) => ({ ...acc, ...cur }), {});

  return (
    <ChartContainer
      config={{
        date: {
          label: "Date",
        },
        ...lines,
      }}
    >
      <LineChart data={postData} className="h-full w-full">
        <CartesianGrid strokeDasharray="3 3" />
        <YAxis
          tick={{ transform: "translate(-8, 0)" }}
          axisLine={false}
          tickLine={false}
        />
        <XAxis
          dataKey="date"
          className="p-10"
          tickFormatter={(v) => formatDate(v as string)}
          tick={{ transform: "translate(0, 12)" }}
          axisLine={false}
          tickLine={false}
        />
        <Legend content={<ChartLegendContent />} />
        {Array.from(keys).map((key) => {
          if (!key.includes(type)) {
            return null;
          }

          const line = lines[key];

          if (!line) {
            return null;
          }

          return (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              strokeWidth={4}
              stroke="currentColor"
              className={line.color}
            />
          );
        })}
      </LineChart>
    </ChartContainer>
  );
}

function RouteComponent() {
  const { campaignId, algoId } = Route.useParams();
  const { data: campaign } = useSuspenseQuery(
    campaignQueryOptions(parseInt(campaignId)),
  );
  const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const isOwner = user && campaign.user_id === user.id;

  return (
    <>
      <CampaignAlgoAnalyticsBlock
        campaignId={campaign.id}
        algo={algo}
        isOwner={isOwner}
      />
      <div className="flex flex-col gap-4">
        <Label>Total Views</Label>
        <div className="min-w-full flex-1 flex flex-col gap-4 border border-slate-6 dark:border-slatedark-6 p-4 rounded-lg">
          <PostGraph type="total_views" />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Label>Users</Label>
        <div className="min-w-full flex-1 flex flex-col gap-4 border border-slate-6 dark:border-slatedark-6 p-4 rounded-lg">
          <PostGraph type="unique_user_views" />
        </div>
      </div>
    </>
  );
}
